import { useEffect, useState } from 'react';

import { PROFESSIONAL_DOWNLOAD_FILES_API_ROUTE } from '~/routes/remix-api.professional.download-files';
import { type DownloadRowGate, DownloadRow, type DownloadRowFile } from './DownloadRow';

interface GroupRequest {
    urlSegment: string;
    itemID: number;
    search: string;
    fileCategories: string[];
}

/**
 * Files already fetched this page view, and the requests still in the air.
 *
 * A group's files do not change while the reader is looking at them, so closing a
 * row and opening it again should cost nothing. Keeping this outside React also
 * collapses the two mounts a development double-render produces into one request.
 */
const filesByRequest = new Map<string, DownloadRowFile[]>();
const inFlight = new Map<string, Promise<DownloadRowFile[]>>();

const requestUrl = ({
    urlSegment,
    itemID,
    search,
    fileCategories,
}: GroupRequest): string => {
    const params = new URLSearchParams();
    params.set('urlSegment', urlSegment);
    params.set('itemId', String(itemID));
    if (search) params.set('q', search);
    for (const category of fileCategories) {
        params.append('fileCategory', category);
    }

    return `${PROFESSIONAL_DOWNLOAD_FILES_API_ROUTE}?${params.toString()}`;
};

/**
 * Fetches a group's files, or hands back the ones already held.
 *
 * Exported so a heading can warm its own group on hover: the row then opens onto
 * rows rather than onto a skeleton.
 */
export const prefetchDownloadGroup = (
    request: GroupRequest,
): Promise<DownloadRowFile[]> => {
    const url = requestUrl(request);
    const held = filesByRequest.get(url);

    if (held) return Promise.resolve(held);

    const pending = inFlight.get(url);

    if (pending) return pending;

    const load = fetch(url, { headers: { Accept: 'application/json' } })
        .then((response) => (response.ok ? response.json() : { files: [] }))
        .then((body: { files?: DownloadRowFile[] }) => {
            const files = body.files ?? [];
            filesByRequest.set(url, files);
            return files;
        })
        .catch(() => [] as DownloadRowFile[])
        .finally(() => inFlight.delete(url));

    inFlight.set(url, load);

    return load;
};

interface DownloadGroupPanelProps extends GroupRequest {
    /**
     * The tab's gate, shared by every group.
     *
     * One per tab rather than one per group: the lead a file earns is recorded
     * once per professional, and a gate per group would each hold their own
     * "already recorded" flag and file a lead per group the reader opens.
     */
    gate: DownloadRowGate;
    /** How many files the group's count promised, used for the loading skeleton. */
    fileCount: number;
    professionalID: number;
    professionalTitle: string;
    showFileColumn: boolean;
}

/**
 * The files inside one open group.
 *
 * Groups are listed with a count and no file rows, so this is what turns a count
 * into rows. It only mounts when its accordion row opens, which is what keeps a
 * professional with a thousand files from fetching them all to draw a list of
 * headings.
 *
 * The current search and category filter ride along with the request, so an opened
 * group shows the files its count promised rather than everything the item owns.
 */
export const DownloadGroupPanel = ({
    urlSegment,
    itemID,
    fileCount,
    search,
    fileCategories,
    professionalID,
    gate,
    professionalTitle,
    showFileColumn,
}: DownloadGroupPanelProps) => {
    const request = { urlSegment, itemID, search, fileCategories };
    const url = requestUrl(request);
    const [files, setFiles] = useState<DownloadRowFile[] | undefined>(() =>
        filesByRequest.get(url),
    );

    useEffect(() => {
        let live = true;

        const held = filesByRequest.get(url);

        if (held) {
            setFiles(held);
            return;
        }

        setFiles(undefined);
        void prefetchDownloadGroup(request).then((loaded) => {
            if (live) setFiles(loaded);
        });

        return () => {
            live = false;
        };
        // The URL carries every input; rebuilding `request` each render must not
        // re-run this.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [url]);

    if (files === undefined) {
        return (
            <ul className="flex flex-col gap-[0.1875rem]" aria-busy="true">
                {Array.from({ length: Math.min(fileCount, 4) }).map(
                    (_, index) => (
                        <li
                            key={index}
                            className="h-[6.25rem] animate-pulse rounded-lg bg-black/5 max-md:h-14"
                        />
                    ),
                )}
            </ul>
        );
    }

    if (files.length === 0) {
        return (
            <p className="py-4 font-sans text-base text-[#666666]">
                These files could not be loaded. Close and reopen to try again.
            </p>
        );
    }

    return (
        <ul className="flex flex-col gap-[0.1875rem]">
            {files.map((file) => (
                <li key={file.ID}>
                    <DownloadRow
                        download={file}
                        professionalID={professionalID}
                        professionalTitle={professionalTitle}
                        showFileColumn={showFileColumn}
                        itemID={itemID}
                        gate={gate}
                    />
                </li>
            ))}
        </ul>
    );
};
